import { router, usePage } from '@inertiajs/vue3';
import { computed, readonly, ref } from 'vue';

type TranslationKey = string;
type LocaleType = 'en' | 'fr' | 'es';

const currentLocale = ref<LocaleType>('en');
const translationCache = new Map<string, string>();
const missingTranslationKeys = new Set<string>();
const AVAILABLE_LOCALES = readonly(['en', 'fr', 'es'] as const);

/**
 * Helper function to safely traverse nested translation object
 */
function getNestedValue(obj: any, keys: string[]): string | null {
    let value = obj;
    for (const key of keys) {
        if (value && typeof value === 'object' && key in value) {
            value = value[key];
        } else {
            return null;
        }
    }
    return typeof value === 'string' ? value : null;
}

/**
 * Replace parameter placeholders in translation string
 */
function replaceParams(text: string, params?: Record<string, string | number>): string {
    if (!params) return text;
    return text.replace(/\{(\w+)\}/g, (match, key) => {
        return params[key]?.toString() || match;
    });
}

export function useTranslations() {
    const page = usePage();

    const pageTranslations = computed(() => {
        return (page.props as any).translations || {};
    });

    const locale = computed<LocaleType>(() => {
        const pageLocale = (page.props as any).locale;
        if (pageLocale && AVAILABLE_LOCALES.includes(pageLocale)) {
            return pageLocale;
        }

        if (typeof window !== 'undefined') {
            const stored = localStorage.getItem('locale');
            if (stored && AVAILABLE_LOCALES.includes(stored as LocaleType)) {
                return stored as LocaleType;
            }
        }

        return 'en';
    });

    const t = (key: TranslationKey, params?: Record<string, string | number>): string => {
        // Create cache key
        const cacheKey = `${locale.value}:${key}`;
        if (translationCache.has(cacheKey)) {
            return replaceParams(translationCache.get(cacheKey)!, params);
        }

        const keys = key.split('.');
        const currentTranslations = pageTranslations.value[locale.value] || pageTranslations.value.en || {};

        // Try current locale first
        let value = getNestedValue(currentTranslations, keys);

        // Fallback to English
        if (!value) {
            const enTranslations = pageTranslations.value.en || {};
            value = getNestedValue(enTranslations, keys);
        }

        // Return key if not found
        const result = value || key;

        if (!value) {
            const missingKey = `${locale.value}:${key}`;
            if (!missingTranslationKeys.has(missingKey)) {
                missingTranslationKeys.add(missingKey);
                // Useful during QA to identify untranslated UI strings.
                console.warn('Missing translation key', {
                    locale: locale.value,
                    key,
                });
            }
        }

        translationCache.set(cacheKey, result);

        return replaceParams(result, params);
    };

    const setLocale = (newLocale: LocaleType) => {
        currentLocale.value = newLocale;
        if (typeof window !== 'undefined') {
            localStorage.setItem('locale', newLocale);
            document.cookie = `locale=${newLocale}; path=/; max-age=31536000; SameSite=Lax`;
            router.visit(window.location.pathname + window.location.search, {
                data: { locale: newLocale },
                preserveState: false,
                preserveScroll: false,
                only: ['translations', 'locale'],
            });
        }
    };

    return {
        t,
        locale,
        setLocale,
        availableLocales: AVAILABLE_LOCALES,
    };
}

export const defaultLocale = 'en';
